import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  NotificationTemplate,
  TemplateVersion,
  TemplateTestRequest,
} from '../types/notification-admin';
import { NotificationType, NotificationPriority } from '../types/user';
import { NotificationService } from './notificationService';

/**
 * NotificationTemplateService
 * Handles all template management operations including CRUD, versioning, and testing
 */
class NotificationTemplateService {
  private readonly collectionName = 'notification_templates';
  private readonly versionCollectionName = 'notification_template_versions';

  /**
   * Get all notification templates
   */
  async getAllTemplates(): Promise<NotificationTemplate[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        orderBy('type', 'asc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => this.mapDocToTemplate(doc.id, doc.data()));
    } catch (error) {
      console.error('Error fetching templates:', error);
      throw new Error('Failed to fetch notification templates');
    }
  }

  /**
   * Get a single template by ID
   */
  async getTemplateById(templateId: string): Promise<NotificationTemplate | null> {
    try {
      const docRef = doc(db, this.collectionName, templateId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return this.mapDocToTemplate(docSnap.id, docSnap.data());
    } catch (error) {
      console.error('Error fetching template:', error);
      throw new Error('Failed to fetch notification template');
    }
  }

  /**
   * Get template by notification type
   */
  async getTemplateByType(type: NotificationType): Promise<NotificationTemplate | null> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('type', '==', type)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      return this.mapDocToTemplate(doc.id, doc.data());
    } catch (error) {
      console.error('Error fetching template by type:', error);
      throw new Error('Failed to fetch notification template');
    }
  }

  /**
   * Create a new notification template
   */
  async createTemplate(
    template: Omit<NotificationTemplate, 'id' | 'version' | 'createdAt' | 'updatedAt'>,
    userId: string
  ): Promise<string> {
    try {
      // Check if template for this type already exists
      const existing = await this.getTemplateByType(template.type);
      if (existing) {
        throw new Error(`Template for type ${template.type} already exists`);
      }

      // Validate template
      this.validateTemplate(template);

      const now = Timestamp.now();
      const templateData = {
        ...template,
        version: 1,
        createdAt: now,
        createdBy: userId,
        updatedAt: now,
        updatedBy: userId,
      };

      const docRef = await addDoc(collection(db, this.collectionName), templateData);

      // Create initial version record
      await this.createVersionRecord(docRef.id, templateData, userId, 'Initial template creation');

      return docRef.id;
    } catch (error) {
      console.error('Error creating template:', error);
      throw error;
    }
  }

  /**
   * Update an existing notification template
   */
  async updateTemplate(
    templateId: string,
    updates: Partial<Omit<NotificationTemplate, 'id' | 'createdAt' | 'createdBy'>>,
    userId: string,
    changeNotes?: string
  ): Promise<void> {
    try {
      const existing = await this.getTemplateById(templateId);
      if (!existing) {
        throw new Error('Template not found');
      }

      // Validate updates
      if (updates.title || updates.message || updates.variables) {
        const updatedTemplate = { ...existing, ...updates };
        this.validateTemplate(updatedTemplate);
      }

      const now = Timestamp.now();
      const newVersion = (existing.version || 1) + 1;

      const updateData = {
        ...updates,
        version: newVersion,
        updatedAt: now,
        updatedBy: userId,
      };

      const docRef = doc(db, this.collectionName, templateId);
      await updateDoc(docRef, updateData);

      // Create version record
      const updatedTemplate = { ...existing, ...updateData };
      await this.createVersionRecord(
        templateId,
        updatedTemplate,
        userId,
        changeNotes || 'Template updated'
      );
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  }

  /**
   * Delete a notification template
   */
  async deleteTemplate(templateId: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, templateId);
      await deleteDoc(docRef);

      // Note: We keep version history even after template deletion for audit purposes
    } catch (error) {
      console.error('Error deleting template:', error);
      throw new Error('Failed to delete notification template');
    }
  }

  /**
   * Toggle template enabled status
   */
  async toggleTemplateStatus(
    templateId: string,
    enabled: boolean,
    userId: string
  ): Promise<void> {
    try {
      await this.updateTemplate(
        templateId,
        { enabled },
        userId,
        `Template ${enabled ? 'enabled' : 'disabled'}`
      );
    } catch (error) {
      console.error('Error toggling template status:', error);
      throw error;
    }
  }

  /**
   * Get version history for a template
   */
  async getTemplateVersions(templateId: string): Promise<TemplateVersion[]> {
    try {
      const q = query(
        collection(db, this.versionCollectionName),
        where('templateId', '==', templateId),
        orderBy('version', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          templateId: data.templateId,
          version: data.version,
          title: data.title,
          message: data.message,
          variables: data.variables || [],
          priority: data.priority,
          enabled: data.enabled,
          changeNotes: data.changeNotes,
          changedBy: data.changedBy,
          changedAt: data.changedAt?.toDate() || new Date(),
        };
      });
    } catch (error) {
      console.error('Error fetching template versions:', error);
      throw new Error('Failed to fetch template version history');
    }
  }

  /**
   * Restore a template to a previous version
   */
  async restoreTemplateVersion(
    templateId: string,
    versionId: string,
    userId: string
  ): Promise<void> {
    try {
      // Get the version to restore
      const versionDoc = await getDoc(doc(db, this.versionCollectionName, versionId));
      if (!versionDoc.exists()) {
        throw new Error('Version not found');
      }

      const versionData = versionDoc.data();

      // Update template with version data
      await this.updateTemplate(
        templateId,
        {
          title: versionData.title,
          message: versionData.message,
          variables: versionData.variables,
          priority: versionData.priority,
          enabled: versionData.enabled,
        },
        userId,
        `Restored to version ${versionData.version}`
      );
    } catch (error) {
      console.error('Error restoring template version:', error);
      throw error;
    }
  }

  /**
   * Test send a notification using a template
   */
  async testSendTemplate(request: TemplateTestRequest): Promise<void> {
    try {
      const template = await this.getTemplateById(request.templateId);
      if (!template) {
        throw new Error('Template not found');
      }

      // Replace variables in title and message
      let title = template.title;
      let message = template.message;

      Object.entries(request.testData).forEach(([key, value]) => {
        const placeholder = `{{${key}}}`;
        title = title.replace(new RegExp(placeholder, 'g'), value);
        message = message.replace(new RegExp(placeholder, 'g'), value);
      });

      // Send test notification
      await NotificationService.createNotification({
        userId: request.recipientUserId,
        type: template.type,
        title: `[TEST] ${title}`,
        message,
        priority: template.priority,
        data: {
          isTest: true,
          originalTemplateId: template.id,
          testData: request.testData,
        },
      });
    } catch (error) {
      console.error('Error sending test notification:', error);
      throw new Error('Failed to send test notification');
    }
  }

  /**
   * Bulk create default templates for all notification types
   */
  async createDefaultTemplates(userId: string): Promise<number> {
    try {
      const batch = writeBatch(db);
      let count = 0;

      const defaultTemplates = this.getDefaultTemplateDefinitions();

      for (const template of defaultTemplates) {
        // Check if template already exists
        const existing = await this.getTemplateByType(template.type);
        if (!existing) {
          const docRef = doc(collection(db, this.collectionName));
          const now = Timestamp.now();

          batch.set(docRef, {
            ...template,
            version: 1,
            createdAt: now,
            createdBy: userId,
            updatedAt: now,
            updatedBy: userId,
          });

          count++;
        }
      }

      if (count > 0) {
        await batch.commit();
      }

      return count;
    } catch (error) {
      console.error('Error creating default templates:', error);
      throw new Error('Failed to create default templates');
    }
  }

  /**
   * Export all templates as JSON
   */
  async exportTemplates(): Promise<string> {
    try {
      const templates = await this.getAllTemplates();
      return JSON.stringify(templates, null, 2);
    } catch (error) {
      console.error('Error exporting templates:', error);
      throw new Error('Failed to export templates');
    }
  }

  /**
   * Import templates from JSON
   */
  async importTemplates(
    jsonData: string,
    userId: string,
    overwriteExisting: boolean = false
  ): Promise<{ imported: number; skipped: number; errors: string[] }> {
    try {
      const templates = JSON.parse(jsonData) as NotificationTemplate[];
      const result = {
        imported: 0,
        skipped: 0,
        errors: [] as string[],
      };

      for (const template of templates) {
        try {
          const existing = await this.getTemplateByType(template.type);

          if (existing && !overwriteExisting) {
            result.skipped++;
            continue;
          }

          if (existing && overwriteExisting) {
            await this.updateTemplate(
              existing.id,
              {
                title: template.title,
                message: template.message,
                variables: template.variables,
                priority: template.priority,
                enabled: template.enabled,
              },
              userId,
              'Imported from JSON'
            );
          } else {
            await this.createTemplate(
              {
                type: template.type,
                title: template.title,
                message: template.message,
                variables: template.variables,
                priority: template.priority,
                enabled: template.enabled,
                createdBy: userId,
                updatedBy: userId,
              },
              userId
            );
          }

          result.imported++;
        } catch (error: any) {
          result.errors.push(`${template.type}: ${error.message}`);
        }
      }

      return result;
    } catch (error) {
      console.error('Error importing templates:', error);
      throw new Error('Failed to import templates');
    }
  }

  /**
   * Validate template data
   */
  private validateTemplate(template: Partial<NotificationTemplate>): void {
    if (!template.title || template.title.trim().length === 0) {
      throw new Error('Template title is required');
    }

    if (!template.message || template.message.trim().length === 0) {
      throw new Error('Template message is required');
    }

    if (template.title.length > 100) {
      throw new Error('Template title must be 100 characters or less');
    }

    if (template.message.length > 500) {
      throw new Error('Template message must be 500 characters or less');
    }

    // Extract variables from template
    const titleVars = this.extractVariables(template.title);
    const messageVars = this.extractVariables(template.message);
    const allVars = [...new Set([...titleVars, ...messageVars])];

    // Validate that declared variables match used variables
    if (template.variables) {
      const declaredVars = new Set(template.variables);
      const usedVars = new Set(allVars);

      // Check for undeclared variables
      const undeclared = allVars.filter((v) => !declaredVars.has(v));
      if (undeclared.length > 0) {
        throw new Error(`Undeclared variables used: ${undeclared.join(', ')}`);
      }
    }
  }

  /**
   * Extract variable placeholders from text
   */
  private extractVariables(text: string): string[] {
    const regex = /\{\{([^}]+)\}\}/g;
    const matches = [];
    let match;

    while ((match = regex.exec(text)) !== null) {
      matches.push(match[1].trim());
    }

    return matches;
  }

  /**
   * Create a version record for template history
   */
  private async createVersionRecord(
    templateId: string,
    templateData: any,
    userId: string,
    changeNotes: string
  ): Promise<void> {
    try {
      await addDoc(collection(db, this.versionCollectionName), {
        templateId,
        version: templateData.version,
        title: templateData.title,
        message: templateData.message,
        variables: templateData.variables || [],
        priority: templateData.priority,
        enabled: templateData.enabled,
        changeNotes,
        changedBy: userId,
        changedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error creating version record:', error);
      // Don't throw - version history is not critical
    }
  }

  /**
   * Map Firestore document to NotificationTemplate
   */
  private mapDocToTemplate(id: string, data: any): NotificationTemplate {
    return {
      id,
      type: data.type,
      title: data.title,
      message: data.message,
      variables: data.variables || [],
      priority: data.priority || NotificationPriority.NORMAL,
      enabled: data.enabled ?? true,
      version: data.version || 1,
      updatedAt: data.updatedAt?.toDate() || new Date(),
      updatedBy: data.updatedBy || 'system',
      createdAt: data.createdAt?.toDate() || new Date(),
      createdBy: data.createdBy || 'system',
    };
  }

  /**
   * Get default template definitions for all notification types
   */
  private getDefaultTemplateDefinitions(): Omit<
    NotificationTemplate,
    'id' | 'version' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'
  >[] {
    return [
      // Authentication
      {
        type: NotificationType.ACCOUNT_APPROVED,
        title: 'Account Approved',
        message: 'Welcome to Martz Family! Your account has been approved.',
        variables: [],
        priority: NotificationPriority.HIGH,
        enabled: true,
      },
      {
        type: NotificationType.ACCOUNT_REJECTED,
        title: 'Account Registration Update',
        message: 'Your account registration could not be approved. Reason: {{reason}}',
        variables: ['reason'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
      {
        type: NotificationType.ROLE_CHANGED,
        title: 'Role Updated',
        message: 'Your role has been changed to {{newRole}}.',
        variables: ['newRole'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
      {
        type: NotificationType.PASSWORD_RESET,
        title: 'Password Reset Requested',
        message: 'A password reset was requested for your account.',
        variables: [],
        priority: NotificationPriority.HIGH,
        enabled: true,
      },
      {
        type: NotificationType.ACCOUNT_SUSPENDED,
        title: 'Account Suspended',
        message: 'Your account has been suspended. Reason: {{reason}}',
        variables: ['reason'],
        priority: NotificationPriority.CRITICAL,
        enabled: true,
      },
      {
        type: NotificationType.ACCOUNT_REACTIVATED,
        title: 'Account Reactivated',
        message: 'Your account has been reactivated. Welcome back!',
        variables: [],
        priority: NotificationPriority.HIGH,
        enabled: true,
      },

      // Content Moderation
      {
        type: NotificationType.CONTENT_FLAGGED,
        title: 'Content Flagged for Review',
        message: 'Your {{contentType}} has been flagged for review.',
        variables: ['contentType'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
      {
        type: NotificationType.CONTENT_APPROVED,
        title: 'Content Approved',
        message: 'Your {{contentType}} has been approved.',
        variables: ['contentType'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
      {
        type: NotificationType.CONTENT_REJECTED,
        title: 'Content Not Approved',
        message: 'Your {{contentType}} could not be approved. Reason: {{reason}}',
        variables: ['contentType', 'reason'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
      {
        type: NotificationType.EDIT_APPROVED,
        title: 'Edit Approved',
        message: 'Your edit to {{itemName}} has been approved.',
        variables: ['itemName'],
        priority: NotificationPriority.LOW,
        enabled: true,
      },
      {
        type: NotificationType.EDIT_REJECTED,
        title: 'Edit Not Approved',
        message: 'Your edit to {{itemName}} could not be approved. Reason: {{reason}}',
        variables: ['itemName', 'reason'],
        priority: NotificationPriority.LOW,
        enabled: true,
      },

      // Social Interactions
      {
        type: NotificationType.NEW_COMMENT,
        title: 'New Comment',
        message: '{{userName}} commented on {{itemName}}.',
        variables: ['userName', 'itemName'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
      {
        type: NotificationType.COMMENT_REPLY,
        title: 'Comment Reply',
        message: '{{userName}} replied to your comment.',
        variables: ['userName'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
      {
        type: NotificationType.COMMENT_MENTION,
        title: 'You Were Mentioned',
        message: '{{userName}} mentioned you in a comment.',
        variables: ['userName'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
      {
        type: NotificationType.PHOTO_TAGGED,
        title: 'Tagged in Photo',
        message: 'You were tagged in a photo by {{userName}}.',
        variables: ['userName'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
      {
        type: NotificationType.RELATIONSHIP_SUGGESTED,
        title: 'Relationship Suggestion',
        message: '{{userName}} suggested a relationship: {{relationship}}',
        variables: ['userName', 'relationship'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },

      // Family Tree Updates
      {
        type: NotificationType.PERSON_ADDED,
        title: 'New Person Added',
        message: '{{userName}} added {{personName}} to the family tree.',
        variables: ['userName', 'personName'],
        priority: NotificationPriority.LOW,
        enabled: true,
      },
      {
        type: NotificationType.PERSON_UPDATED,
        title: 'Person Updated',
        message: '{{userName}} updated information for {{personName}}.',
        variables: ['userName', 'personName'],
        priority: NotificationPriority.LOW,
        enabled: true,
      },
      {
        type: NotificationType.RELATIONSHIP_ADDED,
        title: 'Relationship Added',
        message: '{{userName}} added a new relationship: {{relationship}}',
        variables: ['userName', 'relationship'],
        priority: NotificationPriority.LOW,
        enabled: true,
      },
      {
        type: NotificationType.PHOTO_UPLOADED,
        title: 'New Photo Uploaded',
        message: '{{userName}} uploaded {{photoCount}} new photo(s).',
        variables: ['userName', 'photoCount'],
        priority: NotificationPriority.LOW,
        enabled: true,
      },
      {
        type: NotificationType.MEMORY_ADDED,
        title: 'New Memory Shared',
        message: '{{userName}} shared a memory about {{personName}}.',
        variables: ['userName', 'personName'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },

      // Research & Collaboration
      {
        type: NotificationType.RESEARCH_REQUEST,
        title: 'Research Request',
        message: '{{userName}} requested help researching {{topic}}.',
        variables: ['userName', 'topic'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
      {
        type: NotificationType.RESEARCH_RESPONSE,
        title: 'Research Response',
        message: '{{userName}} responded to your research request.',
        variables: ['userName'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
      {
        type: NotificationType.DOCUMENT_SHARED,
        title: 'Document Shared',
        message: '{{userName}} shared a document: {{documentName}}',
        variables: ['userName', 'documentName'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
      {
        type: NotificationType.COLLABORATION_INVITE,
        title: 'Collaboration Invitation',
        message: '{{userName}} invited you to collaborate on {{projectName}}.',
        variables: ['userName', 'projectName'],
        priority: NotificationPriority.HIGH,
        enabled: true,
      },

      // Reminders
      {
        type: NotificationType.BIRTHDAY_REMINDER,
        title: 'Birthday Reminder',
        message: "{{personName}}'s birthday is {{daysAway}}.",
        variables: ['personName', 'daysAway'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
      {
        type: NotificationType.ANNIVERSARY_REMINDER,
        title: 'Anniversary Reminder',
        message: '{{eventName}} anniversary is {{daysAway}}.',
        variables: ['eventName', 'daysAway'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
      {
        type: NotificationType.TASK_REMINDER,
        title: 'Task Reminder',
        message: 'Reminder: {{taskName}} is due {{dueDate}}.',
        variables: ['taskName', 'dueDate'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
      {
        type: NotificationType.PROFILE_INCOMPLETE,
        title: 'Complete Your Profile',
        message: 'Your profile is {{percentComplete}}% complete. Add more information!',
        variables: ['percentComplete'],
        priority: NotificationPriority.LOW,
        enabled: true,
      },

      // System & Admin
      {
        type: NotificationType.SYSTEM_UPDATE,
        title: 'System Update',
        message: '{{updateMessage}}',
        variables: ['updateMessage'],
        priority: NotificationPriority.HIGH,
        enabled: true,
      },
      {
        type: NotificationType.MAINTENANCE_SCHEDULED,
        title: 'Scheduled Maintenance',
        message: 'System maintenance scheduled for {{maintenanceTime}}.',
        variables: ['maintenanceTime'],
        priority: NotificationPriority.HIGH,
        enabled: true,
      },
      {
        type: NotificationType.ADMIN_BROADCAST,
        title: '{{broadcastTitle}}',
        message: '{{broadcastMessage}}',
        variables: ['broadcastTitle', 'broadcastMessage'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
      {
        type: NotificationType.FEATURE_ANNOUNCEMENT,
        title: 'New Feature Available',
        message: '{{featureName}}: {{featureDescription}}',
        variables: ['featureName', 'featureDescription'],
        priority: NotificationPriority.NORMAL,
        enabled: true,
      },
    ];
  }
}

// Export singleton instance
export const notificationTemplateService = new NotificationTemplateService();
